/**
 * useAgenticChat Hook
 * Manages chat state and AI service communication
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../lib/config';
import { initializeChat, sendAudioMessage as sendAudioMessageApi, sendMessage } from '../services/agenticChatApi';
import type { ChatMessage, ChatSession } from '../types/agenticChat';
import { playWaitingSound } from '../utils/soundEffects';

/**
 * LocalStorage key prefix for chat session storage
 */
const AGENTIC_CHAT_SESSION_KEY_PREFIX = 'agentic-chat-session';

/**
 * Stored chat session structure
 */
interface StoredChatSession {
  aiSessionId: string;
  messages: Array<{
    id: string;
    sender: 'customer' | 'ai' | 'system';
    text: string;
    timestamp: string; // ISO string
    audio?: string | null;
    audioFormat?: string | null;
    actions?: any;
  }>;
  restaurantId: string;
  tableId: string;
  lastUpdated: string; // ISO timestamp
}

/**
 * Get localStorage key for a specific restaurant/table combination
 */
function getStorageKey(restaurantId: string, tableId: string): string {
  return `${AGENTIC_CHAT_SESSION_KEY_PREFIX}-${restaurantId}-${tableId}`;
}

/**
 * Retrieve stored chat session from localStorage
 */
function getStoredSession(restaurantId: string, tableId: string): StoredChatSession | null {
  try {
    const key = getStorageKey(restaurantId, tableId);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }

    const parsed: StoredChatSession = JSON.parse(stored);
    
    // Validate stored session matches current restaurant/table
    if (parsed.restaurantId !== restaurantId || parsed.tableId !== tableId) {
      console.warn('[useAgenticChat] Stored session restaurantId/tableId mismatch, ignoring');
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('[useAgenticChat] Failed to retrieve stored session:', error);
    return null;
  }
}

/**
 * Save chat session to localStorage
 */
function saveSessionToStorage(
  aiSessionId: string,
  messages: ChatMessage[],
  restaurantId: string,
  tableId: string
): void {
  try {
    const key = getStorageKey(restaurantId, tableId);
    
    // Convert messages to serializable format (Date -> ISO string)
    const serializableMessages = messages.map((msg) => ({
      ...msg,
      timestamp: msg.timestamp.toISOString(),
    }));

    const sessionToStore: StoredChatSession = {
      aiSessionId,
      messages: serializableMessages,
      restaurantId,
      tableId,
      lastUpdated: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(sessionToStore));
  } catch (error) {
    console.error('[useAgenticChat] Failed to save session to storage:', error);
  }
}

/**
 * Clear stored chat session from localStorage
 */
function clearStoredSession(restaurantId: string, tableId: string): void {
  try {
    const key = getStorageKey(restaurantId, tableId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[useAgenticChat] Failed to clear stored session:', error);
  }
}

/**
 * Convert stored messages back to ChatMessage format (ISO string -> Date)
 */
function deserializeMessages(storedMessages: StoredChatSession['messages']): ChatMessage[] {
  return storedMessages.map((msg) => ({
    ...msg,
    timestamp: new Date(msg.timestamp),
  }));
}

interface UseAgenticChatProps {
  restaurantId: string;
  tableId: string;
}

interface UseAgenticChatReturn {
  session: ChatSession;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendUserMessage: (message: string) => Promise<void>;
  sendAudioMessage: (audioData: string, format: string) => Promise<void>;
  pushWebRTCMessage: (data: any) => void;
  pushUserMessage: (text: string) => void;
  isActive: boolean;
  endSession: () => void;
  hasStoredSession: boolean;
  handleSessionChoice: (choice: 'continue' | 'new') => void;
}

export function useAgenticChat({
  restaurantId,
  tableId,
}: UseAgenticChatProps): UseAgenticChatReturn {
  const [session, setSession] = useState<ChatSession>({
    aiSessionId: null,
    backendSessionId: null,
    orderId: null,
    restaurantId,
    tableId,
    isActive: true,
    messages: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasStoredSession, setHasStoredSession] = useState(false);
  const [awaitingUserChoice, setAwaitingUserChoice] = useState(false);

  // Track if initialization has been attempted to prevent double-init in React Strict Mode
  const initializationAttempted = useRef(false);

  // Store session ID in ref to avoid stale closure
  const sessionIdRef = useRef<string | null>(null);

  /**
   * Add a message to the chat
   */
  const addMessage = useCallback((message: ChatMessage) => {
    setSession((prev) => ({
      ...prev,
      messages: [...prev.messages, message],
    }));
  }, []);

  /**
   * Push a customer message sent via WebRTC
   */
  const pushUserMessage = useCallback((text: string) => {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'customer',
      text,
      timestamp: new Date(),
    };
    addMessage(userMessage);
  }, [addMessage]);

  /**
   * Push an AI message received via WebRTC
   */
  const pushWebRTCMessage = useCallback((data: any) => {
    const reply = data.reply;
    const sessionId = data.session_id;

    setSession((prev) => {
      const messages = [...prev.messages];
      const lastMsg = messages[messages.length - 1];
      const aiSessionId = prev.aiSessionId || sessionId || null;

      // If the last message is from AI, try to merge this new data
      if (lastMsg && lastMsg.sender === 'ai') {
        const updatedMsg = { ...lastMsg };
        
        if (reply?.output_text) {
          updatedMsg.text = (updatedMsg.text || '') + reply.output_text;
        }
        
        // Only add audio if it's different from what's ALREADY in this message
        // to prevent repeated playback of the same streaming chunk
        if (reply?.output_audio && reply.output_audio !== lastMsg.audio) {
          updatedMsg.audio = reply.output_audio;
          updatedMsg.audioFormat = reply.audio_format || 'mp3';
        }
        
        if (data.actions) {
          updatedMsg.actions = data.actions;
        }

        return {
          ...prev,
          aiSessionId,
          messages: [...messages.slice(0, -1), updatedMsg]
        };
      }

      // Otherwise, create a new AI message for a new turn
      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        sender: 'ai',
        text: reply?.output_text || '',
        timestamp: new Date(),
        audio: reply?.output_audio || null,
        audioFormat: reply?.audio_format || 'mp3',
        actions: data.actions || null,
      };
      
      return {
        ...prev,
        aiSessionId,
        messages: [...messages, aiMessage],
      };
    });
  }, []);

  /**
   * Initialize chat with AI
   */
  const initializeAIChat = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await initializeChat(restaurantId, tableId);
      sessionIdRef.current = response.session_id;
      setSession((prev) => ({ ...prev, aiSessionId: response.session_id }));

      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        sender: 'ai',
        text: response.reply.output_text,
        timestamp: new Date(),
        audio: response.reply.output_audio || null,
        audioFormat: response.reply.audio_format || null,
        actions: response.actions || null,
      };

      addMessage(aiMessage);
    } catch (err) {
      console.error('[useAgenticChat] Failed to initialize chat:', err);
      setError(err instanceof Error ? err.message : 'Failed to start chat.');
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId, tableId, addMessage]);

  /**
   * Send a user message to AI (HTTP)
   */
  const sendUserMessage = useCallback(
    async (messageText: string) => {
      if (!sessionIdRef.current || !messageText.trim()) return;

      setIsLoading(true);
      setError(null);

      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        sender: 'customer',
        text: messageText,
        timestamp: new Date(),
      };
      addMessage(userMessage);

      try {
        const response = await sendMessage(
          sessionIdRef.current,
          restaurantId,
          tableId,
          messageText
        );

        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          sender: 'ai',
          text: response.reply.output_text,
          timestamp: new Date(),
          audio: response.reply.output_audio || null,
          audioFormat: response.reply.audio_format || null,
          actions: response.actions || null,
        };

        addMessage(aiMessage);

        if (response.reply.output_text.toLowerCase().includes('order') &&
            response.reply.output_text.toLowerCase().includes('confirm')) {
          setSession((prev) => ({ ...prev, isActive: false }));
        }
      } catch (err) {
        console.error('[useAgenticChat] Failed to send message:', err);
        setError(err instanceof Error ? err.message : 'Failed to send message.');
      } finally {
        setIsLoading(false);
      }
    },
    [restaurantId, tableId, addMessage]
  );

  /**
   * Send an audio message to AI (HTTP)
   */
  const sendAudioMessage = useCallback(
    async (audioData: string, format: string) => {
      if (!sessionIdRef.current) return;

      setIsLoading(true);
      setError(null);
      playWaitingSound();

      try {
        const response = await sendAudioMessageApi(
          sessionIdRef.current,
          restaurantId,
          tableId,
          audioData,
          format
        );

        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          sender: 'customer',
          text: response.transcript || '[Voice message]',
          timestamp: new Date(),
        };
        addMessage(userMessage);

        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          sender: 'ai',
          text: response.reply.output_text,
          timestamp: new Date(),
          audio: response.reply.output_audio || null,
          audioFormat: response.reply.audio_format || null,
          actions: response.actions || null,
        };

        addMessage(aiMessage);

        if (response.reply.output_text.toLowerCase().includes('order') &&
            response.reply.output_text.toLowerCase().includes('confirm')) {
          setSession((prev) => ({ ...prev, isActive: false }));
        }
      } catch (err) {
        console.error('[useAgenticChat] Failed to send audio message:', err);
        setError(err instanceof Error ? err.message : 'Failed to send audio.');
      } finally {
        setIsLoading(false);
      }
    },
    [restaurantId, tableId, addMessage]
  );

  const handleSessionChoice = useCallback(
    (choice: 'continue' | 'new') => {
      if (choice === 'continue') {
        const storedSession = getStoredSession(restaurantId, tableId);
        if (storedSession && storedSession.aiSessionId) {
          sessionIdRef.current = storedSession.aiSessionId;
          const restoredMessages = deserializeMessages(storedSession.messages).map((msg) => ({
            ...msg,
            audio: null,
            audioFormat: null,
          }));

          setSession((prev) => ({
            ...prev,
            aiSessionId: storedSession.aiSessionId,
            messages: restoredMessages,
          }));

          setHasStoredSession(false);
          setAwaitingUserChoice(false);
        }
      } else {
        clearStoredSession(restaurantId, tableId);
        setHasStoredSession(false);
        setAwaitingUserChoice(false);
        initializationAttempted.current = true;
        initializeAIChat();
      }
    },
    [restaurantId, tableId, initializeAIChat]
  );

  useEffect(() => {
    if (initializationAttempted.current) return;
    if (awaitingUserChoice || hasStoredSession) return;

    if (config.useWebRTC) {
      initializationAttempted.current = true;
      return;
    }

    const storedSession = getStoredSession(restaurantId, tableId);
    if (storedSession && storedSession.aiSessionId) {
      initializationAttempted.current = true;
      setHasStoredSession(true);
      setAwaitingUserChoice(true);
      return;
    }

    if (!session.aiSessionId) {
      initializationAttempted.current = true;
      initializeAIChat();
    }
  }, [restaurantId, tableId, awaitingUserChoice, hasStoredSession, session.aiSessionId, initializeAIChat]);

  useEffect(() => {
    if (session.aiSessionId && session.messages.length > 0) {
      saveSessionToStorage(session.aiSessionId, session.messages, restaurantId, tableId);
    }
  }, [session.aiSessionId, session.messages, restaurantId, tableId]);

  const endSession = useCallback(() => {
    setSession((prev) => ({ ...prev, isActive: false }));
    clearStoredSession(restaurantId, tableId);
  }, [restaurantId, tableId]);

  return {
    session,
    messages: session.messages,
    isLoading,
    error,
    sendUserMessage,
    sendAudioMessage,
    pushWebRTCMessage,
    pushUserMessage,
    isActive: session.isActive,
    endSession,
    hasStoredSession,
    handleSessionChoice,
  };
}
